import { Box, Typography, Paper } from '@mui/material';
import React from 'react';
import ApexSeriesChart from '../../components/Graphs/ApexSeriesChart';
import BirdCountGraph from '../Graphs/BirdCountGraph';
import { seriesDataSmall, colorsTop } from '../../data/seriesData';
import { ReactComponent as InfoIcon } from '../../assets/img/info-icon.svg';
import { DataDescription } from './DataDescription';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorState } from '../common/ErrorState';
import { NoDataState } from '../common/NoDataState';

const reliability = {
  text: 'The reliability of the data collected at the site is impacted by the amount of daylight, and any maintainence causing downtime. The more hours analysed during a day, the more reliable the data is because we are not analysing bird data when it is dark.',
  header: 'What affects the reliability of the data points?',
};

export const BirdCount = React.memo((props: any) => {
  const { isLoading, isError, error, hasData } = props;

  // Show loading state
  if (isLoading) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          minHeight: '800px',
          marginBottom: '7px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <LoadingSpinner 
          message="Loading bird count data..." 
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          minHeight: '800px',
          marginBottom: '7px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <ErrorState 
          title="Failed to load bird count data"
          error={error}
          height="800px"
          showBackground={false}
          onRetry={props.onRetry}
        />
      </Paper>
    );
  }

  // Show no data state
  if (!hasData) {
    return (
      <Paper
        className="custom-chart"
        sx={{
          minWidth: '1000px',
          minHeight: '800px',
          marginBottom: '7px',
          borderRadius: '16px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <NoDataState 
          title="No bird count data available"
          message="No bird detection data found for the selected date range and filters. Try adjusting your selection criteria."
          height="800px"
          showBackground={false}
        />
      </Paper>
    );
  }

  return (
    <Paper
      className="custom-chart"
      sx={{
        minWidth: '1000px',
        maxWidth: '100%',
        minHeight: '800px',
        marginBottom: '7px',
        borderRadius: '16px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(229, 231, 235, 0.8)',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        overflow: 'hidden',
      }}
    >
      <Box className="bar-graph-header">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '23px',
            padding: '20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '11px',
              alignItems: 'center',
              paddingTop: '7px',
            }}
          >
            <InfoIcon />
            <Box
              sx={{
                borderLeft: '2px solid #45009C',
                height: '160px',
              }}
            ></Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ color: '#17052D' }} variant="h6">
              {props.header}
            </Typography>
            <Typography
              sx={{ color: '#8B8296', fontSize: '14px' }}
              variant="body2"
            >
              {props.subHeader}
            </Typography>
            <Box sx={{ position: 'relative', flexGrow: 1 }}>
              <Typography
                sx={{
                  color: '#8B8296',
                  fontSize: '12px',
                  position: 'absolute',
                  top: '30px',
                }}
              >
                {props.type === 'hour' ? (
                  <>
                    The data is sorted from <b>least</b> reliable, 1, to
                    <b> most </b>
                    reliable, 5.
                  </>
                ) : (
                  <>Hours Recorded</>
                )}
              </Typography>
              <Box
                sx={{
                  background: '#E8E8E8',
                  borderLeft: '1px solid #FFFFFF',
                  borderRight: '0px',
                  borderRadius: '20px 0px 0px 20px',
                  width: '5px',
                  height: '19px',
                  position: 'absolute',
                  top: '60px',
                  zIndex: 20,
                }}
              ></Box>
              <Box
                sx={{
                  marginTop: '22.5px',
                  marginLeft: '-23px',
                }}
              >
                <ApexSeriesChart
                  colors={colorsTop}
                  enableDataLabel={true}
                  strokeWidth={1}
                  graphWidth={350}
                  graphData={seriesDataSmall}
                  hoursRecordedBins={props.hoursRecordedBins}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '13px',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  borderRadius: '4px',
                  width: '12px',
                  height: '12px',
                  background: '#8B8296',
                }}
              ></Box>
              <Typography
                sx={{
                  color: '#8B8296',
                  fontSize: '12px',
                }}
                variant="body2"
              >
                Count of birds
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box id="combined-graph" sx={{ flexGrow: 1, width: '100%', overflow: 'hidden' }}>
        <BirdCountGraph
          className={'bar-chart-top'}
          xAxisOptions={props.xAxisOptions}
          yAxisData={props.barGraphBirdCount}
          graphWidth="100%"
          graphHeight={450}
          showGrid={true}
          eventsEnabled={true}
          tickAmount={props.tickAmount}
          type={props.type}
        />
        <BirdCountGraph
          className={'bar-chart-bottom'}
          xAxisOptions={props.xAxisOptions}
          yAxisData={props.barGraphReliable}
          graphWidth="100%"
          colors={props.colors}
          graphHeight={80}
          showGrid={false}
          eventsEnabled={false}
        />
      </Box>
      <DataDescription header={reliability.header} text={reliability.text} />
    </Paper>
  );
});
